import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import { Link, NavLink, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, fieldIssues, uploadFile } from '../api';
import { can, type Me } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatPln } from '../catalog/money';
import { ConflictNotice } from '../ui/ConflictNotice';
import { ContractForm } from './ContractForm';
import { PersonDataForm } from './PersonDataForm';
import { CONTRACTS_KEY, CONTRACTS_RESOURCE, fileUrl, KINDS, PERSON_DATA_RESOURCE, PLACEHOLDERS, plDay, STATES, TEMPLATES_RESOURCE, type Contract, type ContractFile, type ContractState } from './types';
import './contracts.css';
import { leaveGuarded, useUnsavedGuard } from '../ui/unsaved';

const money = (v: number | undefined) => (v === undefined ? '—' : formatPln(v));
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (fieldIssues(e)[0]?.message ?? e.message) : e instanceof Error ? e.message : fallback);
/** The user's scope of an action on contracts ('own': their own person only), or null without the right. */
const scopeOf = (me: Me, action: string): 'all' | 'own' | null => {
  if (me.isOwner) return 'all';
  const found = me.permissions.filter((p) => p.resource === CONTRACTS_RESOURCE && p.action === action);
  return found.some((p) => p.scope === 'all') ? 'all' : found.length > 0 ? 'own' : null;
};
const warsawToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Warsaw' }).format(new Date());

/** "Umowy | Szablony" under the Finanse tab. */
export function ContractsNav({ me }: { me: Me }) {
  if (!can(me, TEMPLATES_RESOURCE, 'view')) return null;
  return (
    <nav className="segment ctr-nav" aria-label="Umowy">
      <NavLink to="/finanse/umowy" end>
        Umowy
      </NavLink>
      <NavLink to="/finanse/umowy/szablony">Szablony</NavLink>
    </nav>
  );
}

/** A table of contracts (the list, a person's card, "Moje umowy"). */
export function ContractsTable({ contracts, showPerson = true, label }: { contracts: Contract[]; showPerson?: boolean; label: string }) {
  const navigate = useNavigate();
  return (
    <div className="table-wrap">
      <table className="table fin-table" aria-label={label}>
        <thead>
          <tr>
            <th scope="col">Numer</th>
            {showPerson && <th scope="col">Osoba</th>}
            <th scope="col">Przedmiot</th>
            <th scope="col">Okres</th>
            <th scope="col">Stan</th>
            <th scope="col" className="num">
              Kwota
            </th>
          </tr>
        </thead>
        <tbody>
          {contracts.map((c) => (
            <tr key={c.id} className="clickable" onClick={() => leaveGuarded(() => navigate(`/finanse/umowy/${c.id}`))}>
              <td>
                <Link to={`/finanse/umowy/${c.id}`} onClick={(e) => e.stopPropagation()}>
                  {c.number}
                </Link>
                <div className="muted ctr-kind">{KINDS[c.kind]}</div>
              </td>
              {showPerson && <td>{c.personName}</td>}
              <td className="ctr-subject">{c.subject}</td>
              <td>
                {plDay(c.dateFrom)} – {plDay(c.dateTo)}
              </td>
              <td>
                <span className={`ctr-state ctr-${c.state}`}>{STATES[c.state]}</span>
              </td>
              <td className="num">{money(c.amountGrosze)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** "Finanse → Umowy": every contract (scope own: one's own), filter by state, "Nowa umowa". */
function ContractsList({ me }: { me: Me }) {
  const navigate = useNavigate();
  const [state, setState] = useState<ContractState | ''>('');
  const [adding, setAdding] = useState(false);
  const list = useQuery({ queryKey: [...CONTRACTS_KEY, 'list', state], queryFn: () => api<{ contracts: Contract[] }>(`/api/contracts${state ? `?state=${state}` : ''}`) });
  const rows = list.data?.contracts ?? [];
  const mayCreate = scopeOf(me, 'create') === 'all';
  return (
    <section aria-label="Umowy" data-feature-id="contracts.list">
      <div className="ctr-head">
        <p className="lede">Umowy o dzieło i zlecenia z ludźmi: z szablonu, z numerem, PDF do podpisu i skan po podpisaniu.</p>
        {mayCreate && !adding && (
          <button className="btn primary" type="button" onClick={() => setAdding(true)}>
            Nowa umowa
          </button>
        )}
      </div>
      {adding && <ContractForm me={me} onSaved={(c) => navigate(`/finanse/umowy/${c.id}`)} onClose={() => setAdding(false)} />}
      <div className="fin-filters">
        <label className="field">
          <span>Stan</span>
          <select value={state} onChange={(e) => setState(e.target.value as ContractState | '')}>
            <option value="">Wszystkie</option>
            {(Object.keys(STATES) as ContractState[]).map((s) => (
              <option key={s} value={s}>
                {STATES[s]}
              </option>
            ))}
          </select>
        </label>
      </div>
      {list.error && <p className="alert" role="alert">{errorText(list.error, 'Nie udało się wczytać umów')}</p>}
      {list.data && rows.length === 0 && <p className="muted">Brak umów.</p>}
      {rows.length > 0 && <ContractsTable contracts={rows} label="Lista umów" />}
    </section>
  );
}

/** "Podpisana": the scan of the signed paper and the day of signing. */
function SignedForm({ contract, onDone }: { contract: Contract; onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [signedOn, setSignedOn] = useState(warsawToday());
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  // the id of the scan once uploaded: a retry after a failed save does not upload it again
  const uploaded = useRef<{ file: File; id: string } | null>(null);
  async function persist(): Promise<boolean> {
    if (!file) return false;
    setBusy(true);
    setError(null);
    try {
      if (uploaded.current?.file !== file) uploaded.current = { file, id: await uploadFile(file) };
      await api(`/api/contracts/${contract.id}/signed`, { method: 'POST', body: { fileId: uploaded.current.id, signedOn, version: contract.version } });
      onDone();
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setBusy(false);
    }
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    void persist();
  }
  // owner 2026-10-23 21:14: a scan chosen and not saved: leaving asks first
  const guardRef = useUnsavedGuard({ dirty: file !== null, save: () => persist() });
  return (
    <form ref={guardRef} className="ctr-signed" onSubmit={submit} aria-label="Podpisana umowa">
      <h3>Podpisana umowa</h3>
      {error !== null && <p className="alert" role="alert">{errorText(error, 'Nie udało się zapisać skanu')}</p>}
      <div className="ctr-grid">
        <label className="field">
          <span>Skan podpisanej umowy</span>
          <input type="file" accept="application/pdf,image/*" required onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </label>
        <label className="field">
          <span>Data podpisania</span>
          <input type="date" required value={signedOn} onChange={(e) => setSignedOn(e.target.value)} />
        </label>
      </div>
      <div className="actions">
        <button className="btn" type="submit" disabled={busy || !file}>
          {busy ? 'Zapisywanie…' : 'Zapisz jako podpisaną'}
        </button>
      </div>
    </form>
  );
}

/** One contract: details, what is missing, "Generuj PDF", the files (every PDF and the scan), signing, cancelling. */
function ContractCard({ id, me }: { id: string; me: Me }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const query = useQuery({ queryKey: [...CONTRACTS_KEY, 'one', id], queryFn: () => api<Contract>(`/api/contracts/${id}`) });
  const files = useQuery({ queryKey: [...CONTRACTS_KEY, 'files', id], queryFn: () => api<{ files: ContractFile[] }>(`/api/contracts/${id}/files`) });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: CONTRACTS_KEY });
  const c = query.data;
  const generate = useMutation({ mutationFn: () => api<Contract>(`/api/contracts/${id}/generate`, { method: 'POST', body: { version: c!.version } }), onSuccess: refresh });
  const cancel = useMutation({ mutationFn: () => api<Contract>(`/api/contracts/${id}`, { method: 'PATCH', body: { state: 'anulowana', version: c!.version } }), onSuccess: refresh });
  const remove = useMutation({
    mutationFn: () => api(`/api/contracts/${id}?version=${c!.version}`, { method: 'DELETE' }),
    onSuccess: () => {
      refresh();
      navigate('/finanse/umowy');
    },
  });
  if (query.isLoading) return <p className="muted" role="status">Wczytywanie…</p>;
  if (query.error || !c) return <p className="alert" role="alert">{errorText(query.error, 'Nie udało się wczytać umowy')}</p>;
  const mayEdit = scopeOf(me, 'edit') === 'all';
  const mayDelete = scopeOf(me, 'delete') === 'all';
  const draft = c.state === 'szkic' || c.state === 'wygenerowana';
  const missing = c.missing ?? [];
  const failed = [generate.error, cancel.error, remove.error].find((e) => e);
  return (
    <section className="ctr-card" aria-label={`Umowa ${c.number}`} data-feature-id="contracts.list">
      <div className="ctr-card-head">
        <div>
          <h2>
            {c.title} {c.number}
          </h2>
          <p className="muted">
            {c.personName}
            {c.eventName && ` · ${c.eventName}`}
          </p>
        </div>
        <span className={`ctr-state ctr-${c.state}`} data-testid="contract-state">
          {STATES[c.state]}
        </span>
      </div>
      {failed instanceof ApiError && failed.status === 409 ? (
        <ConflictNotice
          onReload={() => {
            generate.reset();
            cancel.reset();
            remove.reset();
            refresh();
          }}
        />
      ) : (
        failed && (
          <p className="alert" role="alert">
            {errorText(failed, 'Nie udało się')}
          </p>
        )
      )}
      {editing ? (
        <ContractForm
          me={me}
          contract={c}
          onSaved={() => {
            setEditing(false);
            refresh();
          }}
          onClose={() => {
            setEditing(false);
            refresh();
          }}
        />
      ) : (
        <dl className="ctr-details">
          <dt>Rodzaj</dt>
          <dd>{KINDS[c.kind]}</dd>
          <dt>Przedmiot</dt>
          <dd className="ctr-subject">{c.subject}</dd>
          <dt>Kwota brutto</dt>
          <dd>{money(c.amountGrosze)}</dd>
          <dt>Okres</dt>
          <dd>
            {plDay(c.dateFrom)} – {plDay(c.dateTo)}
          </dd>
          {c.signedOn && (
            <>
              <dt>Podpisana</dt>
              <dd>{plDay(c.signedOn)}</dd>
            </>
          )}
        </dl>
      )}
      {draft && missing.length > 0 && (
        <p className="ctr-missing" role="note">
          <span className="todo-dot" aria-hidden="true" /> Brakuje danych do umowy: {missing.map((k) => PLACEHOLDERS[k] ?? k).join(', ')}.{' '}
          {missing.some((k) => k.startsWith('firma')) && can(me, TEMPLATES_RESOURCE, 'view') && <Link to="/finanse/umowy/szablony">Uzupełnij dane firmy</Link>}
        </p>
      )}
      {mayEdit && draft && !editing && (
        <div className="actions">
          <button className="btn primary" type="button" disabled={generate.isPending || missing.length > 0} onClick={() => generate.mutate()}>
            {generate.isPending ? 'Generowanie…' : c.pdfFileId ? 'Generuj PDF ponownie' : 'Generuj PDF'}
          </button>
          {c.pdfFileId && (
            <a className="btn" href={fileUrl(c.pdfFileId)} target="_blank" rel="noreferrer">
              Pobierz PDF
            </a>
          )}
          <button className="btn" type="button" onClick={() => setEditing(true)}>
            Zmień
          </button>
          <button className="btn ghost" type="button" onClick={() => window.confirm(`Anulować umowę ${c.number}?`) && cancel.mutate()}>
            Anuluj umowę
          </button>
          {mayDelete && (
            <button className="btn ghost ctr-danger" type="button" onClick={() => window.confirm(`Usunąć umowę ${c.number}?`) && remove.mutate()}>
              Usuń
            </button>
          )}
        </div>
      )}
      {!(mayEdit && draft) && c.pdfFileId && (
        <div className="actions">
          <a className="btn" href={fileUrl(c.pdfFileId)} target="_blank" rel="noreferrer">
            Pobierz PDF
          </a>
          {mayEdit && c.state === 'podpisana' && (
            <button className="btn ghost" type="button" onClick={() => window.confirm(`Anulować umowę ${c.number}?`) && cancel.mutate()}>
              Anuluj umowę
            </button>
          )}
        </div>
      )}
      {c.state === 'szkic' && (files.data?.files ?? []).some((f) => f.kind === 'pdf') && (
        <p className="ctr-missing" role="note">
          <span className="todo-dot" aria-hidden="true" /> Umowa zmieniła się po wygenerowaniu — poprzedni PDF jest nieaktualny. Wygeneruj nowy, zanim ktoś ją podpisze.
        </p>
      )}
      {mayEdit && c.state === 'wygenerowana' && !editing && <SignedForm contract={c} onDone={refresh} />}
      {mayEdit && can(me, PERSON_DATA_RESOURCE, 'view') && draft && missing.some((k) => k === 'adres' || k === 'pesel') && (
        <PersonDataForm personId={c.personId} canEdit={can(me, PERSON_DATA_RESOURCE, 'edit')} />
      )}
      {(files.data?.files.length ?? 0) > 0 && (
        <section aria-label="Pliki umowy">
          <h3>Pliki umowy</h3>
          <ul className="ctr-files">
            {files.data!.files.map((f) => (
              <li key={f.fileId}>
                <a href={fileUrl(f.fileId)} target="_blank" rel="noreferrer">
                  {f.kind === 'pdf' ? 'PDF' : 'Skan podpisanej'} — {f.name}
                </a>{' '}
                <span className="muted">
                  {new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', dateStyle: 'short', timeStyle: 'short' }).format(new Date(f.createdAt))}
                  {f.kind === 'pdf' && !f.current && ' · poprzednia wersja'}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </section>
  );
}

/** `/finanse/umowy` (the list) and `/finanse/umowy/:id` (one contract). */
export function ContractsPage() {
  const { me } = useMe();
  const { id } = useParams();
  if (!me) return null;
  return (
    <section className="page wide finance-page">
      {id ? (
        <>
          <p>
            <Link to="/finanse/umowy">← Wszystkie umowy</Link>
          </p>
          <ContractCard id={id} me={me} />
        </>
      ) : (
        <>
          <ContractsNav me={me} />
          {can(me, CONTRACTS_RESOURCE, 'view') ? <ContractsList me={me} /> : <p className="muted">Brak uprawnień do listy umów.</p>}
        </>
      )}
    </section>
  );
}

/** "Umowy" on a person's card (Ludzie): the person's contracts, "Nowa umowa" and the data for contracts. */
export function PersonContracts({ personId, me }: { personId: string; me: Me }) {
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const seeContracts = can(me, CONTRACTS_RESOURCE, 'view');
  const list = useQuery({ queryKey: [...CONTRACTS_KEY, 'person', personId], enabled: seeContracts, queryFn: () => api<{ contracts: Contract[] }>(`/api/contracts?personId=${personId}`) });
  const seeData = can(me, PERSON_DATA_RESOURCE, 'view');
  if (!seeContracts && !seeData) return null;
  return (
    <section className="ctr-person" aria-label="Umowy osoby" data-feature-id="contracts.person_data">
      <div className="ctr-head">
        <h2>Umowy</h2>
        {scopeOf(me, 'create') === 'all' && !adding && (
          <button className="btn" type="button" onClick={() => setAdding(true)}>
            Nowa umowa
          </button>
        )}
      </div>
      {adding && <ContractForm me={me} personId={personId} onSaved={(c) => navigate(`/finanse/umowy/${c.id}`)} onClose={() => setAdding(false)} />}
      {seeContracts && list.data && (list.data.contracts.length === 0 ? <p className="muted">Ta osoba nie ma umów.</p> : <ContractsTable contracts={list.data.contracts} showPerson={false} label="Umowy osoby" />)}
      {seeData && <PersonDataForm personId={personId} canEdit={can(me, PERSON_DATA_RESOURCE, 'edit')} />}
    </section>
  );
}
